import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, Filter, Package, ArrowRight, X, ChevronRight, CheckCircle, MessageCircle, Globe, Truck, Award } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { AnimatedSection, SectionLabel, Seo } from "../components/ui/index";
import { productsAPI, cmsAPI } from "../services/api";
import { getImageUrl } from "../utils/image";
import { useSettings } from "../context/SettingsContext";

function safeParseJson(str, defaultValue) {
  if (!str) return defaultValue;
  let clean = str.replace(/<[^>]*>/g, "");
  clean = clean
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
  try {
    return JSON.parse(clean);
  } catch (e) {
    console.error("Failed to parse JSON setting:", clean, e);
    return defaultValue;
  }
}

const getIconComponent = (iconName) => {
  if (!iconName) return LucideIcons.HelpCircle;
  const formatted = iconName.charAt(0).toUpperCase() + iconName.slice(1);
  return LucideIcons[formatted] || LucideIcons[iconName] || LucideIcons.HelpCircle;
};

// ── Products Listing ─────────────────────────────────────
export function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [cms, setCms] = useState({});

  useEffect(() => {
    Promise.all([
      productsAPI.getAll().then(r => {
        const prods = r.data.products || [];
        setProducts(prods);
        const cats = ["All", ...new Set(prods.map(p => p.category).filter(Boolean))];
        setCategories(cats);
      }),
      cmsAPI.getPage("products").then(r => setCms(r.data.content || {})),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const g = (s, k, fb) => cms?.[s]?.[k]?.value || fb;
  const filtered = products.filter(p => {
    const matchCat = activeCategory === "All" || p.category === activeCategory;
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.category || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="pt-20">
      <Seo page="products" defaultTitle="Products — Sava Fresh Trading FZE" defaultDesc="Premium food products globally sourced — grains, fresh produce, frozen foods, oils, and more." />
      <section className="bg-gradient-to-br from-brand-pale via-white to-accent-tealLight py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <AnimatedSection direction="left">
              <SectionLabel text="Our Products" />
              <h1 className="section-heading mb-4">{g("hero","heading","Premium Food Products, Globally Sourced")}</h1>
              <div className="text-gray-600 leading-relaxed text-lg rich-content" dangerouslySetInnerHTML={{ __html: g("hero","description","Explore our extensive range of premium food products carefully sourced from certified producers across five continents. From staple grains and fresh produce to frozen foods and edible oils — every product in our portfolio meets strict quality and safety standards. We offer flexible packaging, bulk pricing, and full documentation support for buyers worldwide.") }} />
              <div className="flex flex-wrap gap-3 mt-6">
                {(() => {
                  const rawBadgesJson = cms?.badges?.badges_json?.value;
                  const parsedBadges = rawBadgesJson ? safeParseJson(rawBadgesJson, null) : null;
                  const badgesList = parsedBadges || [
                    { icon: "Globe", text: "50+ Countries" },
                    { icon: "Award", text: "ISO Certified" },
                    { icon: "Truck", text: "Global Delivery" }
                  ];
                  return badgesList.map((item, i) => {
                    const IconComp = getIconComponent(item.icon);
                    return (
                      <div key={item.text + i} className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-soft border border-gray-100">
                        <IconComp className="w-4 h-4 text-brand-primary"/>
                        <span className="text-sm font-semibold text-gray-700">{item.text}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </AnimatedSection>

            {/* Right */}
            <AnimatedSection direction="right" delay={200}>
              <div className="relative rounded-3xl overflow-hidden shadow-hover border-4 border-white bg-white group aspect-[4/3] lg:aspect-[1.2]">
                <img
                  src={getImageUrl(g("hero", "imageUrl", "https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=800&q=80"))}
                  alt="Our Products"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <div className="sticky top-16 md:top-20 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-soft py-3">
        <div className="container-custom flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
            <input type="text" placeholder="Search products..." value={search} onChange={e=>setSearch(e.target.value)} className="input-field pl-9 py-2 text-sm w-44"/>
            {search&&<button onClick={()=>setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><X className="w-3.5 h-3.5"/></button>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-gray-400"/>
            {categories.map(cat=>(
              <button key={cat} onClick={()=>setActiveCategory(cat)} className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${activeCategory===cat?"bg-brand-primary text-white shadow-soft":"bg-gray-100 text-gray-600 hover:bg-brand-pale hover:text-brand-primary"}`}>{cat}</button>
            ))}
          </div>
          <span className="ml-auto text-xs text-gray-400">{filtered.length} product{filtered.length!==1?"s":""}</span>
        </div>
      </div>

      <section className="section-pad bg-gray-section">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(6)].map((_,i)=><div key={i} className="bg-white rounded-3xl h-96 animate-pulse"/>)}</div>
          ) : filtered.length===0 ? (
            <div className="text-center py-24"><Package className="w-16 h-16 text-gray-200 mx-auto mb-4"/><h3 className="font-heading font-semibold text-gray-400 text-xl mb-2">No products found</h3><button onClick={()=>{setActiveCategory("All");setSearch("");}} className="btn-ghost mt-2">Clear filters</button></div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((product,i)=>(
                <AnimatedSection key={product.id||product.slug} delay={i*60}>
                  <Link to={`/products/${product.slug}`} className="group bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-hover transition-all duration-500 hover:-translate-y-1 border border-gray-50 flex flex-col block">
                    <div className="h-52 bg-brand-pale overflow-hidden flex-shrink-0 relative">
                      {product.imageUrl ? <img src={getImageUrl(product.imageUrl)} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/> : <div className="w-full h-full flex items-center justify-center"><Package className="w-16 h-16 text-brand-muted"/></div>}
                      {product.featured&&<div className="absolute top-3 right-3 badge-gold text-xs shadow-sm">★ Featured</div>}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      {product.category&&<span className="badge-brand mb-2 text-xs">{product.category}</span>}
                      <h3 className="font-heading font-bold text-gray-900 text-lg mb-2 leading-tight">{product.name}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 flex-1">{product.shortDesc||product.description?.replace(/<[^>]+>/g,"").slice(0,100)}</p>
                      {product.origin&&<p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Globe className="w-3 h-3"/>Origin: {product.origin}</p>}
                      {product.price&&<div className="mt-2 text-brand-primary font-bold font-heading text-sm">{product.price}</div>}
                      <div className="mt-4 flex items-center gap-2 text-brand-primary text-sm font-semibold group-hover:gap-3 transition-all">View Details <ArrowRight className="w-3.5 h-3.5"/></div>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ── Product Detail ────────────────────────────────────────
export function ProductDetailPage() {
  const { slug } = useParams();
  const { get } = useSettings();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    productsAPI.getBySlug(slug)
      .then(async r => {
        setProduct(r.data.product);
        if (r.data.product?.category) {
          const rel = await productsAPI.getAll({ category: r.data.product.category, limit: 4 });
          setRelated((rel.data.products || []).filter(p => p.slug !== slug).slice(0, 3));
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="loader-ring"/></div>;
  if (!product) return (
    <div className="min-h-screen flex flex-col items-center justify-center pt-20 gap-4">
      <Package className="w-16 h-16 text-gray-200"/>
      <p className="text-gray-400 text-lg">Product not found.</p>
      <Link to="/products" className="btn-primary">Browse Products</Link>
    </div>
  );

  // Parse images
  let images = [];
  if (product.imageUrl) images.push(product.imageUrl);
  if (product.images) {
    const extras = product.images.split(",").map(s=>s.trim()).filter(Boolean);
    extras.forEach(u => { if (!images.includes(u)) images.push(u); });
  }

  // Parse specs (JSON key-value)
  let specs = {};
  if (product.specifications) { try { specs = JSON.parse(product.specifications); } catch {} }

  // Parse features (JSON array)
  let features = [];
  if (product.features) { try { features = JSON.parse(product.features); } catch {} }

  const whatsapp = (get("whatsapp_number") || "").replace(/\D/g, "");
  const waMsg = encodeURIComponent(`Hello Sava Fresh, I am interested in: ${product.name}. Please share pricing and availability.`);

  const quickStats = [
    { label: "Origin", value: product.origin, icon: Globe },
    { label: "Packaging", value: product.packaging, icon: Package },
    { label: "Min. Order", value: product.minOrder, icon: Award },
    { label: "Availability", value: product.availability, icon: Truck },
  ].filter(f => f.value);

  return (
    <div className="pt-20">
      <Seo page="product_detail" fallbackObj={product} />

      {/* Breadcrumb */}
      <section className="bg-brand-pale/50 py-4 border-b border-gray-100">
        <div className="container-custom">
          <nav className="text-xs text-gray-400 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-brand-primary transition-colors">Home</Link><ChevronRight className="w-3 h-3"/>
            <Link to="/products" className="hover:text-brand-primary transition-colors">Products</Link><ChevronRight className="w-3 h-3"/>
            <span className="text-brand-primary font-medium">{product.name}</span>
          </nav>
        </div>
      </section>

      <section className="py-8 md:py-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start mb-16">
            
            {/* Left: Images Column (5/12 cols) */}
            <div className="lg:col-span-5 w-full">
              <AnimatedSection direction="left">
                <div className="relative aspect-square bg-gray-50 rounded-3xl overflow-hidden border border-gray-100 shadow-soft mb-4 group">
                  {images.length > 0
                    ? <img src={getImageUrl(images[activeImg])} alt={product.name} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105" />
                    : <div className="w-full h-full flex items-center justify-center"><Package className="w-20 h-20 text-brand-muted" /></div>}
                  {product.featured && <div className="absolute top-4 right-4 bg-brand-primary text-white font-semibold text-xs px-3 py-1.5 rounded-xl shadow-md">★ Featured</div>}
                </div>
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                    {images.map((img, i) => (
                      <button key={i} onClick={() => setActiveImg(i)}
                        className={`flex-shrink-0 w-16 h-16 rounded-2xl overflow-hidden border-2 transition-all ${activeImg === i ? "border-brand-primary shadow-soft scale-95" : "border-transparent opacity-70 hover:opacity-100"}`}>
                        <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </AnimatedSection>
            </div>

            {/* Right: Product Info (7/12 cols) */}
            <div className="lg:col-span-7 space-y-6">
              <AnimatedSection direction="right" delay={150}>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {product.category && <span className="badge-brand text-xs">{product.category}</span>}
                    {product.price && <span className="badge-gold text-xs">{product.price}</span>}
                  </div>
                  <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight">
                    {product.name}
                  </h1>
                </div>

                {/* Short Description */}
                {product.shortDesc && (
                  <p className="text-gray-500 text-base leading-relaxed mt-4">
                    {product.shortDesc}
                  </p>
                )}

                {/* Key Product Information Grid (Above the fold on mobile) */}
                {quickStats.length > 0 && (
                  <div className="bg-gray-50/80 rounded-2xl p-4 border border-gray-100 mt-6 grid grid-cols-2 gap-4">
                    {quickStats.map((stat, i) => {
                      const Icon = stat.icon;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-brand-primary border border-gray-100 shadow-sm flex-shrink-0">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider block">{stat.label}</span>
                            <span className="text-sm font-bold text-gray-800 truncate block">{stat.value}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Action CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mt-8">
                  <Link to={`/contact?product=${encodeURIComponent(product.name)}`} className="btn-primary justify-center py-4 px-8 text-sm flex-1">
                    Request Wholesale Quote <ArrowRight className="w-4 h-4" />
                  </Link>
                  {whatsapp && (
                    <a href={`https://wa.me/${whatsapp}?text=${waMsg}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold text-sm transition-all duration-300 shadow-md shadow-green-500/20 hover:-translate-y-0.5">
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.5-5.729-1.455L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.42 9.864-9.858.002-2.634-1.02-5.11-2.881-6.973C16.634 1.912 14.16 1.882 12.01 1.88c-5.446 0-9.875 4.423-9.879 9.861-.001 1.769.467 3.498 1.357 5.021L2.488 20.35l3.896-1.022.263.126zm10.743-5.316c-.293-.147-1.733-.856-2.002-.954-.269-.099-.465-.148-.659.148-.195.297-.754.954-.926 1.15-.172.197-.344.22-.637.073-.293-.147-1.238-.456-2.361-1.459-.873-.78-1.463-1.743-1.635-2.04-.173-.296-.018-.457.129-.603.132-.131.294-.343.441-.515.146-.171.196-.294.294-.49.098-.196.05-.367-.025-.515-.074-.147-.659-1.59-.903-2.175-.237-.573-.478-.496-.659-.505-.169-.008-.364-.01-.559-.01-.196 0-.514.073-.783.367-.269.294-1.025 1.004-1.025 2.449s1.047 2.839 1.192 3.036c.147.197 2.062 3.149 4.996 4.414.697.3 1.242.48 1.666.615.702.223 1.342.192 1.849.116.564-.085 1.734-.707 1.979-1.39.244-.683.244-1.272.172-1.39-.072-.119-.268-.196-.561-.343z"/>
                      </svg>
                      Inquire on WhatsApp
                    </a>
                  )}
                </div>
              </AnimatedSection>
            </div>
          </div>

          {/* Description & Specifications Tabs/Sections */}
          <div className="grid lg:grid-cols-3 gap-8 items-start mb-16">
            
            {/* Left: Product Specifications & Features (1/3 cols) */}
            <div className="space-y-6">
              {/* Specs */}
              {Object.keys(specs).length > 0 && (
                <AnimatedSection delay={100}>
                  <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-soft">
                    <h3 className="font-heading font-bold text-gray-900 text-lg mb-4 pb-2 border-b border-gray-50">Specifications</h3>
                    <dl className="space-y-3">
                      {Object.entries(specs).map(([k, v]) => (
                        <div key={k} className="flex justify-between items-center text-xs py-1 border-b border-gray-50 last:border-0">
                          <dt className="text-gray-400 font-medium">{k}</dt>
                          <dd className="font-bold text-gray-800 text-right">{String(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </AnimatedSection>
              )}

              {/* Features */}
              {features.length > 0 && (
                <AnimatedSection delay={200}>
                  <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-soft">
                    <h3 className="font-heading font-bold text-gray-900 text-lg mb-4 pb-2 border-b border-gray-50">Highlights</h3>
                    <ul className="space-y-2.5">
                      {features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-xs text-gray-600">
                          <CheckCircle className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                          {feat}
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimatedSection>
              )}
            </div>

            {/* Right: Full Description (2/3 cols) */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                {product.description && (
                  <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-soft">
                    <h2 className="font-heading font-bold text-gray-900 text-xl mb-6 pb-2 border-b border-gray-100">Product Overview</h2>
                    <div 
                      className="rich-content text-gray-600 leading-relaxed ProseMirror" 
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    ></div>
                  </div>
                )}
              </AnimatedSection>
            </div>
          </div>

          {/* Related Products Section */}
          {related.length > 0 && (
            <div>
              <AnimatedSection className="mb-8">
                <h2 className="font-heading font-bold text-gray-900 text-2xl">Related Products</h2>
                <p className="text-gray-400 text-xs mt-1">Explore similar items in the category</p>
              </AnimatedSection>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((rel, i) => (
                  <AnimatedSection key={rel.id} delay={i * 80}>
                    <Link to={`/products/${rel.slug}`}
                      className="group bg-white rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden block hover:-translate-y-1 border border-gray-100">
                      <div className="h-40 bg-brand-pale overflow-hidden relative">
                        {rel.imageUrl 
                          ? <img src={getImageUrl(rel.imageUrl)} alt={rel.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> 
                          : <div className="w-full h-full flex items-center justify-center"><Package className="w-10 h-10 text-brand-muted" /></div>}
                      </div>
                      <div className="p-4">
                        {rel.category && <span className="text-[10px] uppercase font-bold text-brand-primary/60 tracking-wider block mb-1">{rel.category}</span>}
                        <h3 className="font-heading font-bold text-gray-900 text-sm mb-2 group-hover:text-brand-primary transition-colors">{rel.name}</h3>
                        <span className="text-xs text-brand-primary font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          View Details <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </Link>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
